import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MapPin, Eye } from "lucide-react";
import api, { fmt } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "./Dashboard";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import GeoMap from "@/components/GeoMap";
import { TrendChart } from "@/components/Charts";

export default function Locations() {
  const [geofences, setGeofences] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [mapFocus, setMapFocus] = useState(null);
  const [params] = useSearchParams();

  useEffect(() => {
    api.get("/client/geofences").then((r) => {
      setGeofences(r.data);
      const focus = params.get("focus");
      if (focus) {
        const g = r.data.find((x) => x.id === focus);
        if (g) openDetail(g);
      }
    }).catch(() => setGeofences([]));
  }, []);

  const openDetail = (g) => {
    setSelected(g);
    setDetail(null);
    api.get(`/client/geofences/${g.id}`).then((r) => setDetail(r.data)).catch(() => {});
  };

  if (!geofences) return <Skeleton className="h-64 bg-white/5" />;

  return (
    <div className="space-y-6" data-testid="locations-page">
      <div>
        <p className="section-label">Geofence Locations</p>
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-white tracking-tight mt-1">Locations</h2>
      </div>

      <div className="card-surface overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="locations-table">
            <thead>
              <tr className="text-left text-[10px] tracking-widest text-slate-500 uppercase border-b border-white/5 bg-secondary/20">
                <th className="px-5 py-3.5">Location</th><th className="px-5 py-3.5">Address</th>
                <th className="px-5 py-3.5">Latitude</th><th className="px-5 py-3.5">Longitude</th>
                <th className="px-5 py-3.5">Radius</th><th className="px-5 py-3.5">Campaign</th>
                <th className="px-5 py-3.5">Status</th><th className="px-5 py-3.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {geofences.map((g) => (
                <tr key={g.id} className="text-slate-300 hover:bg-white/[0.02]" data-testid={`location-row-${g.id}`}>
                  <td className="px-5 py-3.5 font-semibold text-white">{g.location_name}</td>
                  <td className="px-5 py-3.5 text-xs max-w-[220px] truncate">{g.address}</td>
                  <td className="px-5 py-3.5 font-mono text-xs">{g.latitude.toFixed(4)}</td>
                  <td className="px-5 py-3.5 font-mono text-xs">{g.longitude.toFixed(4)}</td>
                  <td className="px-5 py-3.5">{g.radius_meters}m</td>
                  <td className="px-5 py-3.5 text-xs">{g.campaign_name}</td>
                  <td className="px-5 py-3.5"><StatusBadge status={g.status} /></td>
                  <td className="px-5 py-3.5 text-right space-x-2">
                    <Button size="sm" variant="ghost" className="text-cyan-400 hover:text-cyan-300 h-8" onClick={() => openDetail(g)} data-testid={`view-location-${g.id}`}>
                      <Eye className="w-3.5 h-3.5 mr-1" /> View
                    </Button>
                    <Button size="sm" variant="ghost" className="text-slate-300 h-8" onClick={() => setMapFocus(g)} data-testid={`map-location-${g.id}`}>
                      <MapPin className="w-3.5 h-3.5 mr-1" /> Map
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="bg-popover border-white/10 max-w-2xl" data-testid="location-intelligence-panel">
          <DialogHeader>
            <DialogTitle className="font-display text-white text-lg tracking-wide uppercase">{selected?.location_name}</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div><p className="text-[10px] tracking-widest text-slate-500 uppercase">Geofence</p><p className="text-white font-semibold mt-1">{selected.radius_meters >= 1000 ? `${selected.radius_meters / 1000}km` : `${selected.radius_meters}m`}</p></div>
                <div><p className="text-[10px] tracking-widest text-slate-500 uppercase">Campaign</p><p className="text-white font-semibold mt-1 truncate">{detail?.campaign?.name || "…"}</p></div>
                <div><p className="text-[10px] tracking-widest text-slate-500 uppercase">Status</p><div className="mt-1"><StatusBadge status={selected.status} /></div></div>
                <div><p className="text-[10px] tracking-widest text-slate-500 uppercase">Active</p><p className="text-white font-semibold mt-1 text-xs">{selected.start_date} → {selected.end_date}</p></div>
              </div>
              {detail ? (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[["Impressions", fmt(detail.analytics.totals.impressions)], ["Reach", fmt(detail.analytics.totals.reach)],
                      ["Engagements", fmt(detail.analytics.totals.engagements)], ["Eng. Rate", `${detail.analytics.totals.engagement_rate}%`]].map(([label, v]) => (
                      <div key={label} className="rounded-md border border-white/5 bg-secondary/30 p-3">
                        <p className="font-display font-bold text-cyan-300">{v}</p>
                        <p className="text-[10px] tracking-widest text-slate-500 uppercase mt-0.5">{label}</p>
                      </div>
                    ))}
                  </div>
                  <div>
                    <p className="text-[10px] tracking-widest text-slate-500 uppercase mb-2">Timeline</p>
                    <TrendChart data={detail.analytics.series} keys={[{ key: "impressions", color: "#06b6d4", name: "Impressions" }]} height={200} />
                  </div>
                </>
              ) : (
                <Skeleton className="h-40 bg-white/5" />
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!mapFocus} onOpenChange={() => setMapFocus(null)}>
        <DialogContent className="bg-popover border-white/10 max-w-3xl" data-testid="location-map-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-white">{mapFocus?.location_name}</DialogTitle>
          </DialogHeader>
          {mapFocus && (
            <GeoMap geofences={[mapFocus]} height="420px" testid="location-focus-map" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
